export const DEFAULT_MMP_TARGET_DASHBOARD_ID = 'CompanyPage';
export const MMP_SOURCE_DASHBOARD_ID = 'SingleAssetRepresentation2';

/** True for the MMP portfolio table widget (id historically SARTable). */
export function isMmpPortfolioTableWidget(widget: { id?: string; name?: string } | null | undefined): boolean {
  const id = String(widget?.id || '');
  const name = String(widget?.name || '');
  return id === 'SARTable' || /sar\s*table/i.test(id) || /mimick/i.test(id) || /mimick/i.test(name);
}

/** True when company clicks should deep-link to CompanyPage. */
export function shouldNavigateMmpCompanyClick(args: {
  pagePrismId?: string;
  pageName?: string;
  widgetId?: string;
  widgetName?: string;
  actionType?: string;
  targetDashboardId?: string;
}): boolean {
  if (args.targetDashboardId) return true;
  if (String(args.actionType || '').toLowerCase() === 'navigate') return true;
  if (String(args.pagePrismId) === MMP_SOURCE_DASHBOARD_ID) return true;
  if (/mimick/i.test(String(args.pageName || ''))) return true;
  return isMmpPortfolioTableWidget({ id: args.widgetId, name: args.widgetName });
}

type NavNode = {
  id?: string;
  name?: string;
  categories?: NavNode[];
  items?: NavNode[];
  prisms?: NavNode[];
  dashboards?: NavNode[];
};

function getChildNodes(node: NavNode): NavNode[] {
  // Don't use `||` — empty `categories: []` is truthy and would hide `items`.
  return [...(node.categories || []), ...(node.items || []), ...(node.prisms || []), ...(node.dashboards || [])];
}

/**
 * Resolve a React Router path for a nav leaf id (drawer convention:
 * parent segments use `name`, leaf segment uses `id`).
 */
export function findNavPathById(nodes: NavNode[] | undefined, targetId: string): string | null {
  const needle = String(targetId || '').trim();
  if (!nodes?.length || !needle) return null;

  const walk = (list: NavNode[], segments: string[]): string | null => {
    for (const node of list) {
      if (!node) continue;
      if (node.id != null && String(node.id) === needle) {
        return `/${[...segments, String(node.id)].join('/')}`;
      }
      const children = getChildNodes(node);
      if (!children.length) continue;
      const segment = node.name || node.id;
      const nextSegments = segment ? [...segments, String(segment)] : segments;
      const found = walk(children, nextSegments);
      if (found) return found;
    }
    return null;
  };

  return walk(nodes, []);
}

/** Fallback when the target id is a sibling of the current prism. */
export function replaceLastPathSegment(pathname: string, nextId: string): string {
  const parts = String(pathname || '')
    .split('/')
    .filter(Boolean)
    .map(part => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    });
  if (!parts.length) return `/${nextId}`;
  parts[parts.length - 1] = nextId;
  return `/${parts.join('/')}`;
}

export function buildDashboardPathWithParams(
  path: string,
  params: Record<string, string | number | undefined | null>,
): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}
